import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Check, XCircle, CalendarDays } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail, money } from "@/lib/api";

export default function BookingApproval() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // "confirmed" | "cancelled"

  useEffect(() => {
    api.get(`/booking-approval/${token}`)
      .then((r) => setBooking(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/booking-approval/${token}`, { decision });
      setResult(data.status);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const decided = result || (booking && ["confirmed", "cancelled"].includes(booking.status) ? booking.status : null);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 md:p-10" data-testid="booking-approval-card">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-2xl font-semibold text-foreground mb-8">
          <Saguaro className="h-7 w-7 text-primary" /> The East Valley
        </Link>

        {loading && <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading request…</div>}

        {!loading && error && (
          <div className="text-center" data-testid="approval-error">
            <XCircle className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{error}</p>
          </div>
        )}

        {!loading && !error && booking && (
          <>
            <div className="text-center mb-6">
              <p className="text-primary font-medium text-xs uppercase tracking-widest mb-1">Same-day booking request</p>
              <h1 className="font-serif text-2xl font-semibold text-foreground">{booking.guest_name}</h1>
            </div>
            <div className="rounded-xl bg-accent/50 p-4 space-y-2 text-sm mb-6">
              <div className="flex items-center gap-2 text-foreground"><CalendarDays className="h-4 w-4 text-primary" /> {booking.check_in} → {booking.check_out} · {booking.nights} night(s)</div>
              <div className="flex justify-between text-muted-foreground"><span>Guests</span><span className="text-foreground">{booking.guests}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Total</span><span className="text-foreground">{money(booking.total, booking.currency)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Deposit paid</span><span className="text-primary font-semibold">{money(booking.deposit_amount, booking.currency)}</span></div>
            </div>

            {decided === "confirmed" ? (
              <div className="text-center py-4" data-testid="approval-confirmed">
                <Check className="h-12 w-12 text-secondary mx-auto mb-3" />
                <p className="font-medium text-foreground">Booking approved & confirmed.</p>
                <p className="text-sm text-muted-foreground mt-1">The guest has been notified.</p>
              </div>
            ) : decided === "cancelled" ? (
              <div className="text-center py-4" data-testid="approval-declined">
                <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium text-foreground">Booking declined.</p>
                <p className="text-sm text-muted-foreground mt-1">Remember to refund the deposit in your Stripe dashboard.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button className="rounded-full bg-secondary hover:bg-secondary/90 h-12" disabled={busy} data-testid="approval-approve-btn" onClick={() => decide("approve")}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Approve</>}
                </Button>
                <Button variant="outline" className="rounded-full h-12 text-destructive border-destructive/40" disabled={busy} data-testid="approval-decline-btn" onClick={() => decide("decline")}>
                  Decline
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
